import { Search as SearchIcon } from "@mui/icons-material";
import {
  Button,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@readyfit-common/models";
import { getOrganizationId, handleApiError } from "@readyfit-common/utils";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import {
  Member,
  useLazyGetMembersQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import useStyles from "./NewChatModal.styles";

interface NewChatModalProps {
  onClose: () => void;
  open: boolean;
  onSelectMember: (member: Member) => void;
}

const NewChatModal = ({ onClose, open, onSelectMember }: NewChatModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [getMembers, { data: membersData, isLoading: isLoadingMembers }] =
    useLazyGetMembersQuery();

  const members = membersData?.members || [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (open) {
      getMembers({
        organizationId: getOrganizationId(currentUser?.organizationId),
        search: debouncedSearchQuery,
      }).catch((error) => {
        handleApiError(dispatch, error as ApiError);
      });
    }
  }, [
    open,
    debouncedSearchQuery,
    currentUser?.organizationId,
    getMembers,
    dispatch,
  ]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSelectMember = (member: Member) => {
    setSelectedMember(member);
  };

  const handleConfirm = () => {
    if (selectedMember) {
      onSelectMember(selectedMember);
      setSelectedMember(null);
      setSearchQuery("");
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedMember(null);
    setSearchQuery("");
    onClose();
  };

  const filteredMembers = members.filter(
    (member) => member.memberId !== currentUser?.id,
  );

  const paginatedMembers = filteredMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={handleClose}
    >
      <Grid container rowGap={theme.spacing(3)}>
        <Grid item xs={12}>
          <Typography variant="h5" fontSize={18}>
            New Chat
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder="Search members by name or email"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <List
            sx={{
              maxHeight: 400,
              overflow: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            {isLoadingMembers ? (
              <ListItem>
                <ListItemText primary="Loading members..." />
              </ListItem>
            ) : filteredMembers.length === 0 ? (
              <ListItem>
                <ListItemText primary="No members found" />
              </ListItem>
            ) : (
              paginatedMembers.map((member) => (
                <ListItemButton
                  key={member.memberId}
                  selected={selectedMember?.memberId === member.memberId}
                  onClick={() => handleSelectMember(member)}
                >
                  <ListItemText
                    primary={
                      `${member.firstName} ${member.lastName}` ||
                      member.displayName ||
                      member.email
                    }
                    secondary={member.email}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Grid>

        {filteredMembers.length > rowsPerPage && (
          <Grid item xs={12} display="flex" justifyContent="center">
            <Pagination
              count={Math.ceil(filteredMembers.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, newPage) =>
                handleChangePage(event, newPage - 1)
              }
              color="primary"
              showFirstButton
              showLastButton
            />
          </Grid>
        )}

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(2)}
        >
          <Grid item>
            <Button
              color="secondary"
              className={` ${classes.button} ${classes.backButton}`}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label={"Start Chat"}
              loading={false}
              disabled={!selectedMember}
              onClick={handleConfirm}
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default NewChatModal;
